import { useTranslation } from '../../../shared/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, Button, Space, Typography, message, Tooltip, Tag, Empty } from 'antd';
import { DeleteOutlined, ShoppingCartOutlined, QuestionCircleOutlined, LoadingOutlined, CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import punycode from 'punycode/';
import domainRegistrationService from '../../services/domainRegistrationService';
import { cartService } from '../../services/cartService';
import PageHeader from '../../../shared/components/PageHeader';
import Money from '../../../shared/components/Money';
import { userPath } from '../../../shared/config/paths';

const { Text } = Typography;

const toUnicode = (d) => {
    if (!d || !d.includes('xn--')) return d;
    try { return punycode.toUnicode(d); } catch (e) { return d; }
};
const normalizeDomain = (raw) => {
    let d = String(raw || '').trim().toLowerCase();
    d = d.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '').replace(/\.$/, '');
    return d;
};
const isValidDomain = (d) => /^[^\s.]+(\.[^\s.]+)+$/.test(d);

// 后端 code → 文案模板（中文原文即 i18n key；参数用 {{}}）
const MSG = {
    invalid_domain: '请输入有效的域名',
    rate_limited: '检查太频繁，请稍后再试',
    sales_closed: '域名转入暂未开放',
    tld_unsupported: '暂不支持转入 {{tld}} 域名',
    already_yours: '该域名已在您的账户中',
    already_here: '该域名已在 Yeap Cloud，无法再次转入',
    not_registered: '该域名尚未注册，可以直接注册',
    pending_delete: '域名即将被注册局删除，无法转入',
    redemption: '域名处于赎回期，请先在原注册商赎回',
    pending_transfer: '该域名已有一个转移正在进行中',
    locked: '域名在原注册商处已锁定，请先在原注册商解锁',
    registry_locked: '注册局禁止转移该域名，请联系原注册商解除',
    too_soon: '域名注册（或上次转移）未满 60 天，{{date}} 后可转入',
    auth_code_invalid: '转移码不正确',
    auth_code_required: '请填写转移码',
    not_transferable: '注册商表示该域名暂时不能转入',
    registry_unavailable: '暂时查不到注册局信息，可以继续下单，转入失败会退款',
    registrar_check_unavailable: '注册商预检暂不可用，可以继续下单，转入失败会退款',
    on_hold: '域名当前被暂停解析，转入后需联系注册商处理',
    expired: '域名已过期，建议先在原注册商续费再转入',
    expiring_soon: '域名 {{date}} 到期，建议尽快完成转入',
    registrar_dns: '域名正在使用原注册商的 DNS，转走后可能停止解析，建议先迁到 Yeap DNS',
};
// 后端方法说明（固定文案，供 t() 取译文）
const METHOD_TEXT = {
    ips_tag: '请在原注册商把 IPS Tag 改为 {{tag}}，无需转移码',
    de: '在原注册商获取 AuthInfo 转移码',
};

/**
 * 域名转入：输入域名 → 自动预检（锁定 / 60 天 / 赎回 / 后缀转入方式）→ 填转移码（再验一次）→ 加入购物车
 */
const DomainTransfer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [input, setInput] = useState('');
    const [rows, setRows] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const seq = useRef({});
    const timers = useRef({});

    const msgOf = (item) => {
        const tpl = MSG[item?.code];
        if (!tpl) return item?.message_zh || '';
        return t(tpl, item?.params || {});
    };

    const patch = (domain, p) => setRows(prev => prev.map(r => (r.domain === domain ? { ...r, ...p } : r)));

    const runCheck = async (domain, code) => {
        const n = (seq.current[domain] || 0) + 1;
        seq.current[domain] = n;
        patch(domain, { checking: true });
        let res = null;
        try {
            const r = await domainRegistrationService.precheckTransfer(domain, code || undefined);
            res = r?.data || null;
        } catch (e) {
            res = e?.data || { ok: true, reasons: [], warnings: [{ code: 'registry_unavailable' }] };
        }
        if (seq.current[domain] !== n) return; // 输入已变化，丢弃旧结果
        patch(domain, { checking: false, check: res, checkedCode: code || '' });
    };

    const addDomains = (text = input) => {
        const seen = new Set(rows.map(r => r.domain));
        const added = [];
        String(text || '').split(/\n|;|，/).forEach(line => {
            const parts = line.trim().split(/[\s,]+/).filter(Boolean);
            if (!parts.length) return;
            const domain = normalizeDomain(parts[0]);
            if (!isValidDomain(domain) || seen.has(domain)) return;
            seen.add(domain);
            added.push({ domain, auth_code: parts.slice(1).join(' '), checking: true, check: null, error: null });
        });
        if (!added.length) {
            if (String(text).trim()) message.warning(t('Enter a valid domain'));
            return;
        }
        setRows(prev => [...prev, ...added]);
        setInput('');
        added.forEach(r => runCheck(r.domain, r.auth_code));
    };

    // ?domain=xxx（搜索页「转入」）→ 直接检查
    useEffect(() => {
        const q = searchParams.get('domain');
        if (q) addDomains(q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setAuth = (domain, value) => {
        patch(domain, { auth_code: value, error: null });
        clearTimeout(timers.current[domain]);
        const v = value.trim();
        if (v.length >= 3) timers.current[domain] = setTimeout(() => runCheck(domain, v), 800);
    };

    const remove = (domain) => {
        clearTimeout(timers.current[domain]);
        seq.current[domain] = (seq.current[domain] || 0) + 1;
        setRows(prev => prev.filter(x => x.domain !== domain));
    };

    const needsCode = (r) => (r.check?.tld_method?.method || 'auth_code') === 'auth_code';
    const rowState = (r) => {
        if (r.checking || !r.check) return 'checking';
        if (r.error || !r.check.ok) return 'blocked';
        if (needsCode(r) && !r.auth_code.trim()) return 'need_code';
        return 'ready';
    };
    const ready = rows.length > 0 && rows.every(r => rowState(r) === 'ready');
    const blockedCount = rows.filter(r => rowState(r) === 'blocked').length;

    const handleSubmit = async () => {
        if (!ready) return;
        setSubmitting(true);
        let ok = 0;
        const failed = {};
        for (const r of rows) {
            try {
                const extra = needsCode(r) ? { auth_code: r.auth_code.trim() } : {};
                const res = await cartService.addDomain(r.domain, 'transfer', 1, null, extra);
                if (res?.success) ok += 1; else failed[r.domain] = res;
            } catch (e) {
                const m = e?.error || e?.message || '';
                if (/已在购物车/.test(m)) ok += 1; else failed[r.domain] = e;
            }
        }
        setSubmitting(false);
        const names = Object.keys(failed);
        if (!names.length) {
            navigate(userPath('/billing/cart'));
            return;
        }
        // 加购时的复检结果覆盖到行上（白话原因）
        setRows(prev => prev.filter(r => failed[r.domain]).map(r => {
            const f = failed[r.domain];
            return f?.precheck ? { ...r, check: f.precheck, error: null } : { ...r, error: f?.error || t('Failed to add') };
        }));
        if (ok) message.warning(t('{{ok}} added to cart, {{length}} failed', { ok, length: names.length }));
        else message.error(t('Failed to add to cart'));
    };

    const renderStatus = (r) => {
        const s = rowState(r);
        if (s === 'checking') return <Tag icon={<LoadingOutlined />} color="default">{t('Checking')}</Tag>;
        if (s === 'blocked') return <Tag icon={<CloseCircleFilled />} color="error">{t('Can\'t transfer')}</Tag>;
        if (s === 'need_code') return <Tag color="processing">{t('Enter auth code')}</Tag>;
        return <Tag icon={<CheckCircleFilled />} color="success">{r.check?.info?.auth_code_checked ? t('Auth code OK') : t('Ready to transfer')}</Tag>;
    };

    const renderPrice = (r) => {
        const p = r.check?.price;
        if (!p || !p.amount) return null;
        const label = p.adds_year === false ? t('no renewal included') : t('includes 1 year renewal');
        return (
            <div className="xfer-price">
                <Text strong><Money amount={p.amount} currency={p.currency} /></Text>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>{label}</Text>
            </div>
        );
    };

    const renderNotes = (r) => {
        const c = r.check;
        if (!c) return null;
        const lines = [];
        if (r.error) lines.push(<Text key="err" type="danger">{r.error}</Text>);
        (c.reasons || []).forEach(x => lines.push(
            <Text key={`r-${x.code}`} type="danger"><CloseCircleFilled style={{ marginRight: 6 }} />{msgOf(x)}
                {x.code === 'not_registered' && <Link style={{ marginLeft: 8 }} to={userPath(`/domain-registration/search?q=${encodeURIComponent(r.domain)}`)}>{t('Register it')}</Link>}
            </Text>
        ));
        if (c.ok) {
            (c.warnings || []).forEach(x => lines.push(
                <Text key={`w-${x.code}`} type="warning"><ExclamationCircleFilled style={{ marginRight: 6 }} />{msgOf(x)}
                    {x.link && <Link style={{ marginLeft: 8 }} to={userPath(x.link)}>{t('Set up DNS')}</Link>}
                </Text>
            ));
            if (c.info?.expected_expires_at) {
                lines.push(<Text key="exp" type="secondary">{t('Expires after transfer: {{date}}', { date: c.info.expected_expires_at })}</Text>);
            }
        }
        return lines.length ? <div className="xfer-notes">{lines}</div> : null;
    };

    const renderCodeCell = (r) => {
        const m = r.check?.tld_method;
        if (m && m.method === 'ips_tag') {
            return <Text>{t(METHOD_TEXT.ips_tag, { tag: m.ips_tag })}</Text>;
        }
        if (m && m.method === 'unsupported') return null;
        const isDe = r.check?.tld === 'de';
        return (
            <Input.Password
                value={r.auth_code}
                onChange={e => setAuth(r.domain, e.target.value)}
                onBlur={() => { const v = r.auth_code.trim(); if (v && v !== r.checkedCode) runCheck(r.domain, v); }}
                placeholder={isDe ? t(METHOD_TEXT.de) : t('Auth code (EPP)')}
                autoComplete="off"
                status={r.check?.reasons?.some(x => x.code === 'auth_code_invalid') ? 'error' : undefined}
            />
        );
    };

    return (
        <div>
            <style>{`
                .xfer-row { border: 1px solid var(--ant-color-border-secondary, #f0f0f0); border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; }
                .xfer-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
                .xfer-name { font-weight: 600; font-size: 15px; word-break: break-all; }
                .xfer-price { margin-left: auto; white-space: nowrap; }
                .xfer-del { order: 4; }
                .xfer-code { margin-top: 10px; max-width: 420px; }
                .xfer-notes { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
                @media (max-width: 576px) { .xfer-del { order: 2; margin-left: auto; } .xfer-price { order: 3; margin-left: 0; width: 100%; } .xfer-code { max-width: none; } }
            `}</style>
            <PageHeader
                title={t('Domain transfer')}
                subtitle={t('First unlock the domain at your current registrar and obtain the transfer code')}
                extra={<Button onClick={() => navigate(userPath('/domain-registration/my-domains'))}>{t('My domains')}</Button>}
            />
            <Card bordered={false}>
                <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                    <Input.TextArea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        placeholder={t('example.com, one per line; optionally add a space followed by the transfer code after each domain')}
                        onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); addDomains(); } }}
                        style={{ fontSize: 15 }}
                    />
                    <Button type="primary" onClick={() => addDomains()} style={{ height: 'auto' }}>{t('Add')}</Button>
                </Space.Compact>

                {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Enter the domains to transfer')} />}

                {rows.map(r => (
                    <div className="xfer-row" key={r.domain}>
                        <div className="xfer-top">
                            <span className="xfer-name">{toUnicode(r.domain)}</span>
                            {renderStatus(r)}
                            {renderPrice(r)}
                            <Tooltip title={t('Remove')}>
                                <Button className="xfer-del" type="text" size="small" icon={<DeleteOutlined />} onClick={() => remove(r.domain)} />
                            </Tooltip>
                        </div>
                        {r.check && (r.check.ok || needsCode(r)) && !(r.check.reasons || []).some(x => ['tld_unsupported', 'sales_closed', 'already_yours', 'already_here', 'not_registered'].includes(x.code)) && (
                            <div className="xfer-code">{renderCodeCell(r)}</div>
                        )}
                        {renderNotes(r)}
                    </div>
                ))}

                {rows.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                        {blockedCount > 0 && <Text type="secondary">{t('Remove the domains that can\'t be transferred first')}</Text>}
                        <Tooltip title={t('Sent to the registrar after payment. Usually done in 5–7 days; refunded automatically if it fails')}>
                            <QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)' }} />
                        </Tooltip>
                        <Button
                            type="primary"
                            size="large"
                            icon={<ShoppingCartOutlined />}
                            loading={submitting}
                            disabled={!ready}
                            onClick={handleSubmit}
                        >
                            {t('Add to cart and check out ({{count}})', { count: rows.length })}
                        </Button>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default DomainTransfer;
